import React from 'react';
import Link from 'next/link';
import { Badge } from "@/components/base/badges/badges";
import { Avatar } from "@/components/base/avatar/avatar";
import { BlurImage } from "@/components/base/images/BlurImage";
import { ArrowUpRight } from 'lucide-react';

// --- Original Variants (A, B, D) ---

// Variant A: Side-by-side (Image Left, Content Right)
export const FeaturedPostVariantA = ({ post }) => {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-gray-50 p-2 md:p-8 rounded-[2.5rem] border border-gray-100 group">
            <div className="aspect-[4/3] w-full rounded-[2rem] overflow-hidden relative shadow-sm">
                <Link href={`/blog/${post.id}`}>
                    <BlurImage
                        src={post.image}
                        alt={post.title}
                        fill
                        className="transition-transform duration-700 group-hover:scale-105"
                    />
                </Link>
                <div className="absolute top-4 left-4">
                    <Link href={`/blog/${post.categorySlug}`}>
                        <Badge
                            color="brand"
                            className="bg-white/90 backdrop-blur text-brand-700 border-none shadow-sm hover:bg-white transition-colors cursor-pointer"
                        >
                            {post.category}
                        </Badge>
                    </Link>
                </div>
            </div>
            <div className="p-2 md:p-4">
                <div className="flex items-center gap-3 text-sm font-semibold text-brand-600 mb-4">
                    <span>{post.date}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                </div>
                <Link href={`/blog/${post.id}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 leading-tight group-hover:text-brand-600 transition-colors">
                        {post.title}
                    </h2>
                </Link>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed line-clamp-3">
                    {post.excerpt}
                </p>

                <div className="flex items-center gap-3 mt-4">
                    <Avatar
                        src={post.author?.image}
                        name={post.author?.name}
                        size="md"
                    />
                    <div>
                        <p className="text-sm font-bold text-gray-900">{post.author?.name}</p>
                        <p className="text-xs text-gray-500">Author</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Variant B: Overlay Card - Full background image with text overlay (Original)
export const FeaturedPostVariantB = ({ post }) => {
    return (
        <div className="relative w-full h-[600px] rounded-[2.5rem] overflow-hidden group shadow-md">
            <Link href={`/blog/${post.id}`} className="block w-full h-full">
                <BlurImage
                    src={post.image}
                    alt={post.title}
                    fill={true}
                    className="transition-transform duration-1000 group-hover:scale-105"
                />
            </Link>
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent pointer-events-none" />

            <div className="absolute bottom-0 left-0 w-full p-8 md:p-12 z-10 text-white">
                <Link href={`/blog/${post.categorySlug}`}>
                    <Badge
                        color="brand"
                        className="bg-white/10 backdrop-blur-md text-white border-white/20 mb-6 hover:bg-white/20 transition-colors cursor-pointer"
                    >
                        {post.category}
                    </Badge>
                </Link>

                <Link href={`/blog/${post.id}`}>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white leading-tight max-w-4xl group-hover:text-brand-200 transition-colors">
                        {post.title}
                    </h2>
                </Link>

                <div className="flex flex-col md:flex-row md:items-center gap-6 text-gray-200">
                    <p className="text-lg max-w-2xl line-clamp-2 md:line-clamp-1 opacity-90">
                        {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <span className="w-1 h-1 bg-brand-400 rounded-full"></span>
                            <span>{post.date}</span>
                            <span className="w-1 h-1 bg-brand-400 rounded-full"></span>
                            <span>{post.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Variant D: Minimal Grid - Text Left, Image Right (Original)
export const FeaturedPostVariantD = ({ post }) => {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-4 md:p-8 rounded-[2.5rem] border border-gray-100 bg-white shadow-sm group">
            <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col justify-center p-4">
                <div className="flex items-center gap-2 mb-6">
                    <span className="w-8 h-[2px] bg-brand-600"></span>
                    <span className="text-brand-600 font-bold uppercase tracking-widest text-xs">Featured Story</span>
                </div>

                <Link href={`/blog/${post.id}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 leading-snug group-hover:text-brand-700 transition-colors">
                        {post.title}
                    </h2>
                </Link>

                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                    {post.excerpt}
                </p>

                <div className="flex items-center justify-between border-t border-gray-100 pt-6">
                    <div className="flex items-center gap-3">
                        <Avatar
                            src={post.author?.image}
                            name={post.author?.name}
                            size="sm"
                        />
                        <span className="text-sm font-bold text-gray-900">{post.author?.name}</span>
                    </div>
                    <span className="text-sm text-gray-500 font-medium">{post.readTime} read</span>
                </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2 h-[400px] lg:h-[500px] rounded-[2rem] overflow-hidden relative">
                <Link href={`/blog/${post.id}`} className="block w-full h-full">
                    <BlurImage
                        src={post.image}
                        alt={post.title}
                        fill
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                </Link>
                <div className="absolute top-6 right-6">
                    <Link href={`/blog/${post.categorySlug}`}>
                        <Badge
                            color="gray"
                            className="bg-gray-900/80 backdrop-blur text-white border-none"
                        >
                            {post.category}
                        </Badge>
                    </Link>
                </div>
            </div>
        </div>
    );
};

// --- New Layouts (1, 2, 3) ---

// Layout 1: Overlay Bar
export const FeaturedPostLayout1 = ({ post }) => {
    return (
        <div className="relative w-full aspect-[16/10] md:h-[600px] rounded-[2rem] overflow-hidden group shadow-md">
            <Link href={`/blog/${post.id}`} className="block w-full h-full">
                <BlurImage
                    src={post.image}
                    alt={post.title}
                    fill={true}
                    className="transition-transform duration-700 group-hover:scale-105"
                />
            </Link>

            <div className="absolute bottom-6 left-6 right-6 md:bottom-8 md:left-8 md:right-8">
                <div className="bg-black/60 backdrop-blur-md rounded-2xl p-6 md:p-8 text-white border border-white/10 shadow-lg">
                    <div className="flex flex-col gap-4">
                        <span className="font-semibold text-sm opacity-80">Featured</span>

                        <Link href={`/blog/${post.id}`}>
                            <h2 className="text-2xl md:text-4xl font-bold leading-tight group-hover:text-brand-200 transition-colors">
                                {post.title}
                            </h2>
                        </Link>

                        <p className="text-gray-200 line-clamp-2 md:max-w-3xl opacity-90">
                            {post.excerpt}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Layout 2: Overlap Card
export const FeaturedPostLayout2 = ({ post }) => {
    return (
        <div className="relative mb-32 md:mb-24">
            <div className="w-full aspect-[21/9] md:h-[500px] rounded-[2rem] overflow-hidden relative z-0">
                <Link href={`/blog/${post.id}`}>
                    <BlurImage
                        src={post.image}
                        alt={post.title}
                        fill
                        className="transition-transform duration-700 group-hover:scale-105"
                    />
                </Link>
            </div>

            <div className="relative z-10 -mt-16 md:-mt-24 w-[90%] md:w-[85%] mx-auto bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-gray-100">
                <div className="flex flex-col md:flex-row gap-8 items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-6">
                            <Avatar src={post.author?.image} name={post.author?.name} size="sm" />
                            <span className="text-sm font-bold text-gray-900">{post.author?.name} • {post.date}</span>
                        </div>

                        <Link href={`/blog/${post.id}`}>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-brand-600 transition-colors">
                                {post.title}
                            </h2>
                        </Link>

                        <p className="text-gray-600 text-lg line-clamp-2 mb-8">
                            {post.excerpt}
                        </p>

                        <div className="flex flex-wrap gap-2">
                            <Badge color="brand" className="bg-brand-50 text-brand-700 border-none">{post.category}</Badge>
                        </div>
                    </div>
                    <div className="hidden md:block">
                        <Link href={`/blog/${post.id}`}>
                            <ArrowUpRight className="w-6 h-6 text-gray-400 hover:text-brand-600 transition-colors" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Layout 3: Split Grid
export const FeaturedPostLayout3 = ({ post, recentPosts = [] }) => {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            <div className="flex flex-col group h-full">
                <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden mb-6 relative shadow-sm">
                    <Link href={`/blog/${post.id}`} className="block w-full h-full">
                        <BlurImage
                            src={post.image}
                            alt={post.title}
                            fill
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </Link>
                </div>
                <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-brand-600">
                        <span>{post.author?.name} • {post.date}</span>
                    </div>

                    <Link href={`/blog/${post.id}`}>
                        <h2 className="text-3xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-brand-600 transition-colors">
                            {post.title}
                        </h2>
                    </Link>

                    <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                        {post.excerpt}
                    </p>

                    <div className="flex items-center gap-2 mt-auto">
                        <Badge type="modern" size="sm">{post.category}</Badge>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-8">
                {recentPosts.length > 0 ? recentPosts.map((item) => (
                    <div key={item.id} className="flex gap-6 group/item items-start">
                        <div className="w-32 h-24 md:w-48 md:h-32 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                            <Link href={`/blog/${item.id}`} className="block w-full h-full relative">
                                <BlurImage
                                    src={item.image}
                                    alt={item.title}
                                    fill
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                />
                            </Link>
                        </div>
                        <div className="flex flex-col justify-center py-1">
                            <div className="text-xs font-semibold text-brand-600 mb-2">
                                {item.author?.name} • {item.date}
                            </div>
                            <Link href={`/blog/${item.id}`}>
                                <h3 className="font-bold text-gray-900 leading-snug mb-2 group-hover/item:text-brand-600 transition-colors line-clamp-2">
                                    {item.title}
                                </h3>
                            </Link>
                            <div className="flex gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    {item.category}
                                </span>
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-2xl">
                        Loading recent stories...
                    </div>
                )}
            </div>
        </div>
    );
};
